import { useMediaQuery } from '@mui/material'
import { useMachine } from '@xstate/react'
import cx from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { get, post } from '@js/common/api'
import { TRACKING_MAPPING } from '@js/common/constants'
import { getUrl } from '@js/common/getUrlByDomain'
import { useStatus } from '@js/common/hooks/useStatus'
import { UA } from '@js/common/UA'
import User from '@js/common/user'
import {
  isDebugMode,
  staticAsset,
  supportsLocalStorage
} from '@js/common/utils'

import {
  QuizContext,
  QuizContextType
} from '@f/applications/QuizApp/context/QuizContext'
import {
  convertUser,
  getConversionRequestPayload,
  handleUpdateProfileResponse
} from '@f/applications/QuizApp/partials/ApiHelpers'
import { CheckoutRenderer } from '@f/applications/QuizApp/partials/CheckoutRenderer'
import { publishProfileUpdatedEvent } from '@f/applications/QuizApp/partials/Events'
import PageHeader from '@f/applications/QuizApp/partials/PageHeader'
import {
  prefillRadioStep,
  trackPrefilledFirstStep
} from '@f/applications/QuizApp/partials/prefillStep'
import { QUIZ_STEP_TYPES } from '@f/applications/QuizApp/partials/quizConstants'
import { QuizHelpBlock } from '@f/applications/QuizApp/partials/QuizHelpBlock'
import QuizNavigation, {
  QuizNavigationWithTransition
} from '@f/applications/QuizApp/partials/QuizNavigation'
import QuizProgress from '@f/applications/QuizApp/partials/QuizProgress'
import { QuizStepTransition } from '@f/applications/QuizApp/partials/QuizStepTransition'
import { RenderStep } from '@f/applications/QuizApp/partials/RenderStep'
import {
  AutofillValidationState,
  NestedStepValue,
  ProfileRequestPayload,
  ProfileRequestResponse,
  ProspectTrack,
  QuizConfig,
  QuizStep,
  QuizStepId,
  SelectedOptionNumber,
  StepType
} from '@f/applications/QuizApp/partials/types'
import { QUIZ_API_URL } from '@f/applications/QuizApp/partials/url'
import { useCheckoutContentOverrides } from '@f/applications/QuizApp/partials/useCheckoutContentOverrides'
import {
  accumulatePayloadsFromAllSteps,
  calculateFinancialDetailsSliderDefaults,
  computeProfileUpdatePayload,
  isFinanceTypeQuiz,
  isHouseTypeQuiz
} from '@f/applications/QuizApp/partials/utils'
import { CheckoutTransitionOut } from '@f/applications/QuizApp/questions/checkout/checkoutTransitionOut/CheckoutTransitionOut'
import {
  redirectToDashboard,
  redirectToFinancePRQ,
  redirectToSuccessPage,
  saveDataToLocalStorage
} from '@f/applications/QuizApp/questions/checkout/checkoutTransitionOut/redirectUtils'
import QuestionSuccess from '@f/applications/QuizApp/questions/QuestionSuccess'
import { initializeQuizMachine } from '@f/applications/QuizApp/stateMachine/initializeQuizMachine'

import { Nullable } from '@f-fertighaus/applications/Next/types/utility'
import { errorLogger } from '@f-fertighaus/utils/errorLogger'
import { getHashParams } from '@f-fertighaus/utils/hashParams'

import { PageFooter } from './partials/PageFooter'
import { QuizTrustElements } from './partials/QuizTrustElements'
import { QuestionSuccessFinance } from './questions/QuestionSuccessFinance'

export interface PredictedConversionValue {
  conversion_value: number | null
  contract_probability: number | null
  prospect_track: ProspectTrack
}

const QuizApp = ({ quizConfig }: { quizConfig: QuizConfig }) => {
  const machine = useMemo(() => initializeQuizMachine(quizConfig), [])
  const hashParams = getHashParams(window.location.hash)
  const [state, send] = useMachine(machine, {
    devTools: isDebugMode
  })
  const [magicToken, setMagicToken] = useState('')
  const isMobileView = useMediaQuery('(max-width: 901px)')
  const [publicId, setPublicId] = useState<Nullable<string>>(null)
  const autofillValidation = useRef<AutofillValidationState>()
  const [autofillValues, setAutofillValues] = useState({})
  const [
    prospectConversionValueCallStatus,
    setProspectConversionValueCallStatus
  ] = useStatus('initial')
  const [prospectConversionValue, setProspectConversionValue] = useState<Pick<
    PredictedConversionValue,
    'prospect_track' | 'contract_probability_base'
  > | null>(null)
  const [validation, setValidation] = useState({
    has_valid_email: false,
    has_valid_phone: false
  })
  const quizCompletionPhase = state.value

  const [displayTransitionOut, setDisplayTransitionOut] = useState({
    visible: true,
    step: 'validation'
  })

  const UAData = UA.getLS()
  const redirectToUA = React.useRef(
    UA.isLoggedIn() &&
      !quizConfig.exclusiveVendorNid &&
      UA.userHasState() &&
      ((isFinanceTypeQuiz(quizConfig.conversionType) &&
        UAData.financing_handover_consent) ||
        (isHouseTypeQuiz(quizConfig.conversionType) &&
          UAData.vendor_handover_consent))
  )

  if (redirectToUA.current) {
    window.location = `${getUrl(
      'userAccount'
    )()}uebersicht/#m=post-conv-request`
  }

  const usersLocation = User.getLocation().postcode

  let prefilledSteps = [...quizConfig.steps]

  const [prefilledData] = useState(() => {
    try {
      const prefilledValues = JSON.parse(localStorage.getItem('quiz'))
      if (prefilledValues) {
        prefilledSteps = prefilledSteps.map(step => {
          step.values.forEach(value => {
            if (prefilledValues[value.name]) {
              value['value'] = prefilledValues[value.name]
            }
          })

          return step
        })

        localStorage.removeItem('quiz')
        return prefilledValues
      }
      return {}
    } catch (e) {
      return {}
    }
  })

  const [displayTransitionIn, setDisplayTransitionIn] = useState(
    quizConfig.transitionInMultiPoint
  )

  const [steps, updateSteps] = useState<QuizStep[]>(() =>
    prefilledSteps.map((step, index) => {
      const locationStep = step.values.find(value => value.name === 'bauort')
      if (usersLocation && locationStep) {
        locationStep.value = usersLocation
      }

      if (step.type === QUIZ_STEP_TYPES.budget) {
        const { value, ...rest } = step.values[0]
        return {
          ...step,
          values: [rest]
        }
      }

      if (
        step.type === QUIZ_STEP_TYPES.financialDetails &&
        prefilledData.budget_range
      ) {
        step.values = calculateFinancialDetailsSliderDefaults(
          step,
          prefilledData.budget_range
        )
      }

      const firstStepValueRegexp = /1-(1|2|3|4)/
      const shouldPrefillFirstStep =
        index === 0 && hashParams.q?.match(firstStepValueRegexp)
      if (shouldPrefillFirstStep) {
        const selectedOptionNumber = Number(
          hashParams.q.split('-')[1]
        ) as SelectedOptionNumber
        const prefilledStep = prefillRadioStep(step, selectedOptionNumber)

        trackPrefilledFirstStep(
          prefilledStep,
          state.context.flow.flow_information
        )
        return prefilledStep
      }

      /* Tag last checkout step, so we know when we need to display a transition screen */
      if (step.checkout && !quizConfig.steps[index + 1]?.checkout) {
        step.isLastCheckout = true
      }

      return step
    })
  )
  const stepsBeforeCheckout = steps.findIndex(step => step.checkout)

  const currentStep = steps[state.context.currentStepIndex]

  useEffect(() => {
    const isCheckoutLoader =
      quizCompletionPhase === 'checkout' && displayTransitionIn

    const getConversionValue = async () => {
      const stepValues = accumulatePayloadsFromAllSteps(steps)
      const predictedProspectValueParams = [
        'budget_range',
        'property',
        'construction_start',
        'house_buyer_stage'
      ]
      const requestValues = predictedProspectValueParams.reduce(
        (acc, param) => {
          const value = stepValues.find(v => v.name === param)
          if (value) {
            acc[param] = value.value
          }
          if (param === 'budget_range') {
            acc.budget = value.value
            delete acc[param]
          }
          if (param === 'construction_start') {
            acc.urgency = value.value
            delete acc[param]
          }
          return acc
        },
        {}
      )

      const updateCustomContentInCheckout = (
        exProspectTrack: ProspectTrack
      ) => {
        updateSteps(currentSteps =>
          currentSteps.map((step, index) => {
            if (step.checkout) {
              const override = step.custom_content?.find(({ id }) =>
                exProspectTrack.includes(id)
              )

              if (override) {
                return {
                  ...step,
                  cta_copy: override.cta_copy || step.cta_copy,
                  heading: override.heading || step.heading
                }
              }
              return {
                ...step,
                cta_copy: quizConfig.steps[index].cta_copy,
                heading: quizConfig.steps[index].heading
              }
            }

            return step
          })
        )
      }
      setProspectConversionValueCallStatus('fetching')
      try {
        const response: PredictedConversionValue = await get(
          'data-services/predicted-prospect-value',
          {
            ...requestValues,
            target_usecase: quizConfig.exclusiveVendorNid
              ? quizConfig.exclusiveVendorNid
              : 'core'
          },
          {}
        )
        if (response.prospect_track) {
          setProspectConversionValueCallStatus('resolved')
          setProspectConversionValue({
            contract_probability_base: response.contract_probability,
            prospect_track: response.prospect_track
          })
          localStorage.setItem(
            'custom_checkout_content_id',
            response.prospect_track
          )
          _wt({
            category: 'api',
            action: 'success',
            location: `checkout-transition`,
            values: [
              { type: 'conversion_value', value: response.conversion_value }
            ],
            indicates: ['user-input'],
            flow_information: { ...state.context.flow.flow_information }
          })

          const prospectTrack = response.prospect_track
          updateCustomContentInCheckout(prospectTrack)

          return response
        }
        throw new Error('no-response')
      } catch (error) {
        setProspectConversionValueCallStatus('error')
        localStorage.setItem('custom_checkout_content_id', 'handover')
        updateCustomContentInCheckout('handover')
        setProspectConversionValue({
          prospect_track: 'handover',
          contract_probability_base: null
        })
        _wt({
          category: 'api',
          action: 'fail',
          location: `checkout-transition`,
          values: [
            {
              type: 'predicted-prospect-value',
              value: error?.message === 'no-response' ? 'no_response' : 'error'
            }
          ],
          flow_information: { ...state.context.flow.flow_information }
        })
      }
    }

    if (
      isCheckoutLoader &&
      prospectConversionValueCallStatus === 'initial' &&
      quizConfig.conversionType === 'house'
    ) {
      getConversionValue()
    }
  }, [
    quizConfig.exclusiveVendorNid,
    quizConfig.conversionType,
    quizCompletionPhase,
    displayTransitionIn,
    steps,
    state.context.flow.flow_information,
    currentStep._wtName,
    prospectConversionValueCallStatus,
    quizConfig.steps,
    setProspectConversionValueCallStatus
  ])

  const updateValues = ({
    stepID,
    valuesToSave
  }: {
    stepID: QuizStepId
    valuesToSave: { value: string; name: string }[]
  }) => {
    const autofillValuesToSave = valuesToSave.reduce((acc, { name, value }) => {
      if (name.includes('autofill')) {
        acc[name] = value
      }
      return acc
    }, {})
    if (Object.keys(autofillValuesToSave).length) {
      setAutofillValues(prev => ({ ...prev, ...autofillValuesToSave }))
    }

    if (stepID === 'valuation_interest') {
      if (supportsLocalStorage()) {
        localStorage.setItem('seller_quiz', JSON.stringify(valuesToSave))
      }
    }

    updateSteps(currentSteps => {
      return currentSteps.map(step => {
        if (step.id === stepID) {
          step.values.map(value => {
            const valueToSave = valuesToSave.find(v => v.name === value.name)
            if (valueToSave) {
              value.value = valueToSave.value
            }

            return value
          })
        }

        return step
      })
    })
  }

  const { flow } = state.context

  const move = ({ to = 'next', triggerSubmit = true }) => {
    switch (to) {
      case 'next':
        if (triggerSubmit && !currentStep.checkout && !currentStep.validation) {
          const indicates = ['funnel-step-change']
          if (currentStep.type === QUIZ_STEP_TYPES.budget) {
            indicates.push('user-input')
          }
          _wt({
            category: 'form',
            action: 'submit',
            location: `question-${currentStep._wtName}`,
            values: currentStep.values.map(({ value, name }) => ({
              type: TRACKING_MAPPING[name]?.label || name,
              value: value
            })),
            indicates,
            flow_information: { ...flow.flow_information }
          })
        }

        send({ type: 'SUBMIT' })
        break
      case 'prev':
        send({ type: 'PREV_STEP' })
        break
    }
  }

  const updateProfile = (stepValues: NestedStepValue[]) => {
    post<ProfileRequestResponse, ProfileRequestPayload>(
      QUIZ_API_URL.updateProfile,
      computeProfileUpdatePayload({
        stepValues,
        conversionType: quizConfig.conversionType,
        publicId,
        exclusiveVendorNid: quizConfig.exclusiveVendorNid,
        flowType: quizConfig.flowType,
        autofillValidation: autofillValidation.current,
        conversionValue: prospectConversionValue
      })
    )
      .then(response => {
        if (
          (currentStep.validation ||
            currentStep.type ===
              QUIZ_STEP_TYPES.exclusiveLeadCheckoutAddress) &&
          response.has_valid_email &&
          response.has_valid_phone
        ) {
          if (quizConfig.successRedirectPageUrl) {
            window.location.assign(quizConfig.successRedirectPageUrl)
          } else {
            move({ to: 'next', triggerSubmit: false })
          }
        }
        setValidation({
          has_valid_email: response.has_valid_email,
          has_valid_phone: response.has_valid_phone
        })
        setPublicId(response.public_id)
        publishProfileUpdatedEvent(response)
        return response
      })
      .then(response => {
        handleUpdateProfileResponse(response)

        if (response?.indicates) {
          setConversionIndicator(response.indicates)
        }
      })
      .catch(e => errorLogger(e))
  }

  const isStepWithConversion = (stepType: StepType) => {
    return stepsWithConversion.includes(stepType)
  }

  const stepsWithConversion = [
    QUIZ_STEP_TYPES.checkoutAddressPhone,
    QUIZ_STEP_TYPES.exclusiveLeadCheckoutGenderNameEmailPhone,
    QUIZ_STEP_TYPES.exclusiveLeadCheckoutEmailPhone,
    QUIZ_STEP_TYPES.sellerLeadCheckoutEmailPhone,
    QUIZ_STEP_TYPES.sellerLeadCheckoutNameEmailPhone,
    QUIZ_STEP_TYPES.exclusiveLeadCheckoutPhone,
    QUIZ_STEP_TYPES.checkoutAddress,
    QUIZ_STEP_TYPES.checkoutPersonalDetails,
    QUIZ_STEP_TYPES.checkoutPersonalDetailsBirthdate,
    QUIZ_STEP_TYPES.checkoutFixedPersonalDetailsBirthdate,
    QUIZ_STEP_TYPES.checkoutEmailPhoneAddress
  ]

  const sendRequest = () => {
    const stepValues = accumulatePayloadsFromAllSteps(steps)
    const shouldTriggerConversion = isStepWithConversion(currentStep.type)

    if (shouldTriggerConversion) {
      const conversionRequestPayload = getConversionRequestPayload({
        stepValues,
        usersLocation,
        trackingFlow: flow,
        exclusiveVendorNid: quizConfig.exclusiveVendorNid,
        conversionType: quizConfig.conversionType,
        flowType: quizConfig.flowType,
        autofillValidation: autofillValidation.current,
        conversionValue: prospectConversionValue
      })
      convertUser(conversionRequestPayload, quizConfig.conversionType).then(
        res => {
          if (!res.has_valid_email || !res.has_valid_phone) {
            setDisplayTransitionOut(prev => ({ ...prev, visible: false }))
          }

          if (res.JWT) {
            handleUpdateProfileResponse(res)
            setMagicToken(res.JWT?.magic_token || '')
          }

          if (res.public_id) {
            setPublicId(res.public_id)
          }

          setValidation({
            has_valid_email: res.has_valid_email,
            has_valid_phone: res.has_valid_phone
          })
        }
      )
    } else {
      updateProfile(stepValues)
    }
  }

  const refreshTransitionInStatus = () => {
    if (quizCompletionPhase === 'pre-checkout') {
      setDisplayTransitionIn(quizConfig.transitionInMultiPoint)
    }
  }
  useEffect(refreshTransitionInStatus, [
    state.context.currentStepIndex,
    quizConfig.conversionType
  ])

  useCheckoutContentOverrides(steps, updateSteps, quizConfig)

  const [conversionIndicator, setConversionIndicator] = useState<
    Nullable<string>
  >(null)

  const redirectAlreadyActivatedUserToUAWithConversionModal = React.useRef(
    isHouseTypeQuiz(quizConfig.conversionType) &&
      UA.isLoggedIn() &&
      !quizConfig.exclusiveVendorNid &&
      UA.getCurrentUserState() === 'activation'
  )

  if (redirectAlreadyActivatedUserToUAWithConversionModal.current) {
    window.location = `${getUrl(
      'userAccount'
    )()}uebersicht/#m=act-anbieter-empfehlung`
    return null
  }

  const isNewMobileCheckoutDesign =
    (quizCompletionPhase === 'checkout' ||
      (quizCompletionPhase === 'validation' &&
        !displayTransitionOut.visible)) &&
    (!isFinanceTypeQuiz(quizConfig.conversionType) ||
      currentStep.type === QUIZ_STEP_TYPES.checkoutValueMessage ||
      currentStep.type === QUIZ_STEP_TYPES.checkoutPersonalDetailsBirthdate ||
      currentStep.type ===
        QUIZ_STEP_TYPES.checkoutFixedPersonalDetailsBirthdate) &&
    isMobileView &&
    !displayTransitionIn

  const displayCheckoutWithImage =
    (quizCompletionPhase === 'checkout' ||
      (quizCompletionPhase === 'validation' &&
        !displayTransitionOut.visible)) &&
    (!isFinanceTypeQuiz(quizConfig.conversionType) ||
      currentStep.type === QUIZ_STEP_TYPES.checkoutValueMessage ||
      currentStep.type === QUIZ_STEP_TYPES.checkoutPersonalDetailsBirthdate ||
      currentStep.type ===
        QUIZ_STEP_TYPES.checkoutFixedPersonalDetailsBirthdate) &&
    !isMobileView &&
    !displayTransitionIn

  function moveToSuccessStep() {
    setDisplayTransitionOut(prev => ({
      ...prev,
      visible: false
    }))
    send({ type: 'NEXT_STEP' })
  }

  function handleCheckoutSuccess() {
    const allowUARedirect = !quizConfig.overrideAccountRedirect
    const isIMCheckout =
      window.__DOMAIN__ === 'immonet' || window.__DOMAIN__ === 'immowelt'

    const redirectOverrideWithSuccessPage =
      quizConfig.successRedirectPageUrl && quizConfig.overrideAccountRedirect
    const redirectOverride =
      !quizConfig.successRedirectPageUrl && quizConfig.overrideAccountRedirect

    const hasNoFinancialInterest =
      steps.find(
        (step: QuizStep) => step.id === 'financing_consultation_interest'
      )?.values[0].value === 'no'

    const sellerQuizPostAllowedValues = ['house', 'apartment', 'maybe']

    const valuationInterestValue =
      steps
        ?.find((step: QuizStep) => step.id === 'valuation_interest')
        ?.values[0]?.value?.find(v => v.name === 'valuation-interest')?.value ||
      ''

    const redirectToSellerQuizPost = sellerQuizPostAllowedValues.includes(
      valuationInterestValue
    )

    const isSellerTypeQuiz = quizConfig.conversionType === 'seller-conversion'

    if (isSellerTypeQuiz) {
      redirectToDashboard()
    } else if (redirectOverride) {
      moveToSuccessStep()
    } else if (redirectOverrideWithSuccessPage) {
      redirectToSuccessPage(quizConfig.successRedirectPageUrl)
    } else if (!isFinanceTypeQuiz(quizConfig.conversionType)) {
      if (!isIMCheckout && redirectToSellerQuizPost) {
        const propertyTypeValue =
          steps
            ?.find((step: QuizStep) => step.id === 'valuation_interest')
            ?.values[0]?.value?.find(v => v.name === 'property-type')?.value ||
          ''
        const propertyTypeHashParam = {
          apartment: '1-1',
          house: '1-2'
        }
        const propertyTypeHashParamValue = propertyTypeHashParam[
          propertyTypeValue
        ]
          ? `#q=${propertyTypeHashParam[propertyTypeValue]}`
          : ''
        saveDataToLocalStorage(steps)
        window.location = getUrl('sellerQuiz')(`${propertyTypeHashParamValue}`)
        return
      }

      if (hasNoFinancialInterest) {
        if (isIMCheckout) {
          window.location = getUrl('userAccount')(magicToken)
          return
        }

        redirectToDashboard()
        return
      }
      redirectToFinancePRQ({ steps, ...flow.flow_information })
    } else if (allowUARedirect) {
      if (isIMCheckout) {
        window.location = getUrl('userAccount')(magicToken)
        return
      }

      redirectToDashboard()
    }
  }

  const handleTransitionOutEnd = () => {
    if (quizConfig.exclusiveVendorNid) {
      if (quizConfig.successRedirectPageUrl) {
        window.location.assign(quizConfig.successRedirectPageUrl)
      } else {
        setDisplayTransitionOut(prev => ({ ...prev, visible: false }))
        send({ type: 'NEXT_STEP' })
      }
      return
    }

    handleCheckoutSuccess()
  }

  if (redirectToUA.current) return null

  const context = {
    data: {
      transitionInMultiPoint: quizConfig.transitionInMultiPoint,
      eventIndicates: conversionIndicator,
      conversionType: quizConfig.conversionType,
      exclusiveVendorNid: quizConfig.exclusiveVendorNid,
      successRedirectPageUrl: quizConfig.successRedirectPageUrl,
      debounceModal: quizConfig.debounceModal,
      steps,
      stepIndex: state.context.currentStepIndex,
      visitedStepsAmount: state.context.visitedStepsAmount,
      prefilledData,
      validation,
      publicId,
      autofillValues: autofillValues
    },
    tileStyle: quizConfig.tileStyle,
    isNewMobileCheckoutDesign,
    displayCheckoutWithImage,
    displayLoader: state.context.displayLoader,
    displayStep: state.context.displayStep,
    actions: {
      updateValues,
      move,
      sendRequest,
      setDisplayTransitionOut,
      setAutofillValidation: (newValue: AutofillValidationState) => {
        autofillValidation.current = {
          ...autofillValidation.current,
          ...newValue
        }
      }
    },
    tracking: {
      flow
    }
  } as QuizContextType

  return (
    <QuizContext.Provider value={context}>
      <PageHeader />
      <div
        className={cx({
          'QuizApp__checkout-wrapper': displayCheckoutWithImage
        })}>
        <div
          className={cx(
            'f-overflow f-d-flc f-pos-re QuizApp is-page',
            isNewMobileCheckoutDesign ? 'f-c-ba-whi' : 'f-c-ba-bac025',
            displayCheckoutWithImage && 'QuizApp--checkout-with-image'
          )}>
          {!currentStep.hidesProgressBar && (
            <QuizProgress
              label={quizConfig.progressBarLabel}
              current={state.context.currentStepIndex + 1}
              visited={state.context.visitedStepsAmount}
              total={stepsBeforeCheckout}
            />
          )}
          {
            {
              'pre-checkout': (
                <div className='[ f-pos-rel f-m-h-auto f-fl-expand ] QuizApp-wrap QuizApp-overflow'>
                  <div className='[ f-d-flr ]'>
                    <QuizStepTransition currentStep={currentStep}>
                      <RenderStep step={currentStep} />
                      {currentStep.helpBlockTeaser &&
                        currentStep.helpBlockAnswer && (
                          <QuizHelpBlock
                            teaser={currentStep.helpBlockTeaser}
                            answer={currentStep.helpBlockAnswer}
                          />
                        )}
                      {isHouseTypeQuiz(quizConfig.conversionType) &&
                        (currentStep.trustElement ? (
                          <>
                            <QuizNavigationWithTransition
                              currentStep={currentStep}
                            />
                            <QuizTrustElements
                              trustElement={currentStep.trustElement}
                            />
                          </>
                        ) : (
                          <QuizNavigationWithTransition
                            currentStep={currentStep}
                          />
                        ))}
                    </QuizStepTransition>
                  </div>
                  {!isHouseTypeQuiz(quizConfig.conversionType) && (
                    <QuizNavigation currentStep={currentStep} />
                  )}
                </div>
              ),
              checkout: (
                <CheckoutRenderer
                  currentStep={currentStep}
                  displayCheckoutWithImage={displayCheckoutWithImage}
                  displayTransitionIn={displayTransitionIn}
                  setDisplayTransitionIn={setDisplayTransitionIn}
                  isNewMobileCheckoutDesign={isNewMobileCheckoutDesign}
                />
              ),
              validation: displayTransitionOut.visible ? (
                <CheckoutTransitionOut
                  setDisplayTransitionOut={setDisplayTransitionOut}
                  transitionType={displayTransitionOut.step}
                  onTransitionEnd={handleTransitionOutEnd}
                />
              ) : (
                <CheckoutRenderer
                  currentStep={currentStep}
                  displayCheckoutWithImage={displayCheckoutWithImage}
                  displayTransitionIn={displayTransitionIn}
                  setDisplayTransitionIn={setDisplayTransitionIn}
                  isNewMobileCheckoutDesign={isNewMobileCheckoutDesign}
                />
              ),
              success: displayTransitionOut.visible ? (
                <CheckoutTransitionOut
                  setDisplayTransitionOut={setDisplayTransitionOut}
                  transitionType='success'
                  onTransitionEnd={handleTransitionOutEnd}
                />
              ) : (
                <div className='QuizApp-success'>
                  {quizConfig.displayStandaloneAffordabilityScreen ? (
                    <QuestionSuccessFinance step={currentStep} />
                  ) : (
                    <QuestionSuccess step={currentStep} />
                  )}
                </div>
              )
            }[quizCompletionPhase]
          }
        </div>
        {displayCheckoutWithImage && (
          <div
            className='QuizApp__background'
            style={{
              backgroundImage: `url(${
                currentStep.checkoutPreviewImageDesktop ||
                staticAsset('/assets/checkout-background-img.png')
              })`
            }}
          />
        )}
      </div>
      <PageFooter />
    </QuizContext.Provider>
  )
}

export { QuizApp }
